// standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
/**
 * The tour's card and ring (see tour.ts). The card lives in the documents list's top slot
 * (and in the empty list after the export) once the tour has begun; while the five are read,
 * TourStart's window speaks instead. The ring is drawn over the real control the card talks
 * about, a moment after the words: 3 px in the tour's own orange, pulsing (owner, 2026-10-04: the
 * thin ring in the accent color blended in; 4 px and a bouncing "Click here" tag were too much).
 * When a step names several it walks from one to the next and rests on the last.
 * On their own first job the same card offers the tour once (TourOffer) and then runs it there.
 */
import { hostHas } from '../shared/host-capabilities';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowLeft,
  ArrowRight,
  FolderOpen,
  FolderOutput,
  History,
  Play,
  Pointer,
} from 'lucide-react';
import {
  ADVANCE_AFTER,
  TOUR_EVENT,
  TOUR_FINISHED_KEY,
  TOUR_OFFER_KEY,
  TOUR_TAKEN_KEY,
  TOUR_WATCHED_KEY,
  advanceOnOf,
  canGoBack,
  markTourFlag,
  nextTourStep,
  previousTourStep,
  readTour,
  readTourFlag,
  shouldOfferOwnTour,
  tourPosition,
  tourStep,
  writeTour,
  type TourContext,
  type TourState,
  type TourStepId,
  type TourVia,
} from './tour';
import { clickWithoutTelemetry } from './telemetry';
import { tourTelemetry } from './tour-telemetry';
import { MILESTONE_EVENT, hasMilestone } from './onboarding';
import { openSetupSection } from './FirstReviewGuide';
import { NEW_JOB_BACKUP_DAYS } from '../shared/export-presentation';
import type { SimulatedScanResult } from '../shared/desktop-api';
// PAIGE: the sample tour's Your files: exported for real before the card says so (paige/TourDownload.tsx).
import { EXPORTING_SLOWLY, TourDownload, useTourExport } from '../paige/TourDownload';
import './tour.css';

export const TOUR_LEAVE_EVENT = 'capturepoint-tour-leave';
export const TOUR_OWN_JOB_EVENT = 'capturepoint-tour-own-job';

export function useTour(): TourState | null {
  const [tour, setTour] = useState<TourState | null>(() => readTour());
  useEffect(() => {
    const update = () => setTour(readTour());
    window.addEventListener(TOUR_EVENT, update);
    window.addEventListener('storage', update);
    return () => {
      window.removeEventListener(TOUR_EVENT, update);
      window.removeEventListener('storage', update);
    };
  }, []);
  return tour;
}

/**
 * How many documents wait right now, read from the list's title row (data-waiting: the documents asked
 * about); null while the row isn't on screen (the list redrawing), which is not the same as nothing waiting.
 */
const waitingNow = (): number | null => {
  const count = document.querySelector<HTMLElement>('.spine-title-row')?.dataset.waiting;
  const value = count == null ? NaN : Number(count);
  return Number.isFinite(value) ? value : null;
};
/** Whether the job is in Calibration, from the tag beside its name. */
const calibratingNow = () => Boolean(document.querySelector('.spine-calibration-tag'));

/** The last count read, kept current while the card is up (null until the tab has been seen). */
function useWaiting(): number | null {
  const [waiting, setWaiting] = useState(waitingNow);
  useEffect(() => {
    const timer = window.setInterval(() => {
      const now = waitingNow();
      if (now !== null) setWaiting(now);
    }, 400);
    return () => window.clearInterval(timer);
  }, []);
  return waiting;
}

/**
 * The tour's export folder, set before anything is read: no picker, the job's own default,
 * Documents › Paige Jobs › <job> › exports (offerDefaultExportDestination: "<job> (2)" or
 * "exports (2)" when an earlier tour's is taken), with the exceptions folder its save gives it
 * beside it (ADR-0096). It goes through the ordinary Export route, sending only when someone
 * clicks Export, so the Export step is one click and nothing leaves before it (owner, 2026-10-04).
 */
export async function presetTourExport(jobId: string, jobName: string): Promise<void> {
  const api = window.capturePointDesktop;
  // PAIGE: every Paige job has Download, and its documents go as soon as they are ready
  // (`desktop-export`); there is no folder to pick, and Your files downloads them (tour.ts).
  if (!api || !hostHas('desktop-export')) return;
  const [stored, starter] = await Promise.all([
    api.getExportSettings(jobId),
    api.getStarterFilingPlan(jobId),
  ]);
  const offered = await api.offerDefaultExportDestination(jobId, jobName);
  await api.saveExportSettings({
    jobId,
    destinationToken: offered.token,
    triggerMode: 'manual',
    pdfProfile: stored?.pdfProfile ?? 'searchable-pdf',
    retentionDays: stored ? stored.retentionDays : NEW_JOB_BACKUP_DAYS,
    filingPlan: stored?.filingPlan ?? starter,
    scheduleLocalTime: null,
    scheduleTimeZone: null,
    enabled: true,
    destinationKind: 'filesystem',
    destinationBindingId: null,
  });
}

/**
 * The tour's five documents go straight in (owner, 2026-10-04: the tour lands in the product,
 * with no scanner window and no processing screen). The simulated feed runs at its fastest
 * model, with nothing on screen; the documents are then read as any capture is.
 */
export async function startTourScan(
  jobId: string,
  corpus: string,
  documents: string[],
): Promise<SimulatedScanResult['capture'] | null> {
  const api = window.capturePointDesktop;
  if (!api) return null;
  const catalog = await api.getSimulatedScanners(corpus);
  const fastest = [...catalog.models].sort((a, b) => b.pagesPerMinute - a.pagesPerMinute)[0];
  if (!fastest) throw new Error("The tour's sample documents could not be fed.");
  const result = await api.startSimulatedScan({
    jobId,
    modelId: fastest.id,
    corpus: catalog.tray?.corpus ?? corpus,
    documents,
  });
  return result.capture ?? null;
}

/** The words on a control, kept current (the Export step names the button exactly as it reads). */
function useLabel(selector: string): string | undefined {
  const [label, setLabel] = useState<string | undefined>(undefined);
  useEffect(() => {
    const read = () => setLabel(document.querySelector(selector)?.textContent?.trim() || undefined);
    read();
    const timer = window.setInterval(read, 500);
    return () => window.clearInterval(timer);
  }, [selector]);
  return label;
}

/** How many documents the pane holds, from the list's title row. */
const totalNow = () =>
  Number(document.querySelector<HTMLElement>('.spine-title-row')?.dataset.documents) || 0;

/**
 * What has gone out since the tour began (their own job's Export step moves on when something
 * has; Your files says how many), the latest export to open, and whether it went to a folder.
 */
function useOwnExport(
  jobId: string | undefined,
  since: string | undefined,
  active: boolean,
  own: boolean,
): { exported: number; exportId?: string; folder: boolean } {
  const [state, setState] = useState<{ exported: number; exportId?: string; folder: boolean }>({
    exported: 0,
    folder: true,
  });
  // PAIGE: on Paige what went out of their own job is its documents delivered, each with its
  // package (paige/TourDownload.tsx), counted as the sample tour's are; nothing opens a folder.
  const delivered = useTourExport(jobId, active && own && !hostHas('desktop-export'));
  useEffect(() => {
    // PAIGE: what went out is the desktop's export record (`desktop-export`); Paige counts above.
    if (!active || !jobId || !since || !hostHas('desktop-export')) return;
    let live = true;
    const read = async () => {
      const api = window.capturePointDesktop;
      if (!api) return;
      try {
        // Only documents that arrived: a failed, uncertain or still-delivering one was not exported.
        const [page, settings] = await Promise.all([
          api.listExportedDocuments({ jobId, exportedFromUtc: since, state: 'exported' }),
          api.getExportSettings(jobId),
        ]);
        const exportId = page.documents[0]?.exportId;
        if (live)
          setState({
            exported: exportId ? page.total : 0,
            exportId,
            folder: (settings?.destinationKind ?? 'filesystem') === 'filesystem',
          });
      } catch {
        /* The next look tries again. */
      }
    };
    void read();
    const timer = window.setInterval(() => void read(), 2000);
    return () => {
      live = false;
      window.clearInterval(timer);
    };
  }, [jobId, since, active]);
  return hostHas('desktop-export') ? state : { exported: delivered.exported, folder: false };
}

/**
 * Moves on from `from`, and only from it: a second trigger for the same step can't skip the next one. `via`: how
 * (Next, the step's own control, or by itself), which telemetry reports with the step.
 */
export function advanceTour(
  from: TourStepId,
  known: Partial<TourContext> = {},
  via: TourVia = 'next',
): void {
  const tour = readTour();
  if (!tour || tour.step !== from) return;
  const next = nextTourStep(tour.step, { waiting: waitingNow() ?? 0, ...known }, tour.own);
  if (next) writeTour({ ...tour, step: next }, undefined, via);
}

/** Back from `from`, and only from it, to the step before (see previousTourStep). Nothing is undone. */
export function backTour(from: TourStepId, known: Partial<TourContext> = {}): void {
  const tour = readTour();
  if (!tour || tour.step !== from) return;
  const previous = previousTourStep(tour.step, { waiting: waitingNow() ?? 0, ...known }, tour.own);
  if (previous) writeTour({ ...tour, step: previous }, undefined, 'back');
}

const bold = (text: string) =>
  text.split(/\*\*(.+?)\*\*/).map((part, index) => (index % 2 ? <b key={index}>{part}</b> : part));
const leave = () => window.dispatchEvent(new Event(TOUR_LEAVE_EVENT));

/** True once the selector matches something on screen. */
function usePresent(selector: string | undefined): boolean {
  const [present, setPresent] = useState(
    () => !selector || Boolean(document.querySelector(selector)),
  );
  useEffect(() => {
    if (!selector) {
      setPresent(true);
      return;
    }
    let timer = 0;
    const look = () => {
      setPresent(Boolean(document.querySelector(selector)));
      timer = window.setTimeout(look, 300);
    };
    look();
    return () => window.clearTimeout(timer);
  }, [selector]);
  return present;
}

/** The card, with its ring. Shows only in the tour's job (the sample one, or their own), once the tour has begun. */
export function TourCard({ jobId }: { jobId?: string }) {
  const tour = useTour();
  const own = Boolean(tour?.own);
  const waiting = useWaiting();
  // The Export step glows on the export card's button, the sample's and their job's alike (one list since 2026-10-30).
  // PAIGE: Paige's export line takes the card's look and its button (paige/HostExportPanel.tsx).
  const exportLabel = useLabel('.export-ready button');
  // What went out: their job's Export step moves on by it, and Your files says how many (the sample's too).
  const ownExport = useOwnExport(
    tour?.jobId,
    tour?.startedAt,
    tour?.step === 'files' || (own && tour?.step === 'export'),
    own,
  );
  // The main button's words, so Confirming names it exactly as it reads.
  const confirmLabel = useLabel('.document-confirm-bar .document-confirm-label');
  const context: TourContext = {
    waiting: waiting ?? 0,
    total: totalNow(),
    calibrating: calibratingNow(),
    exportLabel,
    exported: ownExport.exported,
    folder: ownExport.folder,
    confirmLabel,
  };
  const step = tour ? tourStep(tour.step, own) : null;
  // PAIGE: Your files waits for the five to be exported, as CapturePoint's waits for its export.
  const sampleExport = useTourExport(tour?.jobId, Boolean(step?.download) && tour?.jobId === jobId);
  const ready = usePresent(step?.waitFor) && (!step?.download || sampleExport.ready);
  const after = step?.advanceAfter ?? ADVANCE_AFTER;
  // Moves on by itself only on a count actually read from the tab, a moment after it changed.
  const finished = waiting !== null && Boolean(step?.done?.({ ...context, waiting }));
  useEffect(() => {
    if (!finished || !tour) return;
    const timer = window.setTimeout(
      () => advanceTour(tour.step, { exported: ownExport.exported }, 'auto'),
      after,
    );
    return () => window.clearTimeout(timer);
  }, [finished, tour?.step]); // eslint-disable-line react-hooks/exhaustive-deps
  // The sample tour counts as taken once it reaches Your turn: their first job won't offer it again.
  // Either tour reaching its end counts as watched: the places that offer it say "Watch the tour again".
  // And as finished, for session telemetry (TOUR_FINISHED_KEY, tour.ts).
  useEffect(() => {
    if (tour?.step !== 'turn') return;
    markTourFlag(TOUR_WATCHED_KEY);
    markTourFlag(TOUR_FINISHED_KEY);
    if (!tour.own) markTourFlag(TOUR_TAKEN_KEY);
  }, [tour?.step]); // eslint-disable-line react-hooks/exhaustive-deps
  // Until Start the tour, TourStart's window speaks: nothing is said about documents not yet read.
  if (!tour || !step || tour.jobId !== jobId || !tour.ready) return null;
  // TOUR_TAKEN_KEY is the sample tour's end only (tour.ts), as at Your turn above.
  const finish = () => {
    writeTour(null);
    markTourFlag(TOUR_FINISHED_KEY);
    if (!tour.own) markTourFlag(TOUR_TAKEN_KEY);
  };
  const openExport = async () => {
    if (ownExport.folder && ownExport.exportId)
      await window.capturePointDesktop
        ?.openExportedDocument(ownExport.exportId, 'root')
        .catch(() => undefined);
    else openSetupSection('export', 'activity');
    const from = tour.step;
    window.setTimeout(() => advanceTour(from, { exported: ownExport.exported }, 'control'), after);
  };
  const { index, count } = tourPosition(tour.step);
  const last = index === count - 1;
  const back = canGoBack(tour.step, own);
  const goBack = () => backTour(tour.step, { exported: ownExport.exported });
  // The glow belongs to the card: it shows only while the tour's job is open and reading is done.
  // The bar is the only count on screen (owner, October 4: "7 of 10" said the same twice);
  // screen readers hear the step in the card's name.
  // A fresh glow for each step: its walk starts over at the first stop, and nothing carries over from the last step.
  return (
    <>
      <TourOverlay key={tour.step} />
      <section
        className="tour-card"
        aria-label={`Tour: ${step.section}, step ${index + 1} of ${count}`}
        aria-live="polite"
      >
        <header>
          <span className="tour-title">
            <span className="tour-mark" aria-hidden="true">
              <Play size={11} />
            </span>
            {step.section}
          </span>
          {!last && (
            <button
              type="button"
              className="tour-leave"
              data-telemetry="tour-leave"
              onClick={leave}
            >
              Leave tour
            </button>
          )}
        </header>
        <div className="tour-bar" aria-hidden="true">
          {Array.from({ length: count }, (_, at) => (
            <i key={at} className={at < index ? 'done' : at === index ? 'now' : ''} />
          ))}
        </div>
        <p className="tour-say" key={tour.step}>
          {ready
            ? bold(step.say(context))
            : step.download && sampleExport.slow
              ? bold(EXPORTING_SLOWLY)
              : step.whileWaiting}
        </p>
        {ready && step.todo && (
          <p className="tour-todo" key={`${tour.step}-todo`}>
            <Pointer size={17} aria-hidden="true" />
            <span>{bold(step.todo(context))}</span>
          </p>
        )}
        {(back || last || step.openExport || step.download || (step.next && ready)) && (
          <div className="tour-actions">
            {/* Back, at the left of Next, on every card but the first (owner, 2026-10-04). */}
            {back && (
              <button
                type="button"
                className="tour-back"
                data-telemetry="tour-back"
                onClick={goBack}
              >
                <ArrowLeft size={14} aria-hidden="true" />
                Back
              </button>
            )}
            {last ? (
              own ? (
                <button
                  type="button"
                  className="primary-button small"
                  data-telemetry="tour-done"
                  onClick={finish}
                >
                  Done
                </button>
              ) : (
                <button
                  type="button"
                  className="primary-button small"
                  data-telemetry="tour-own-job"
                  onClick={() => window.dispatchEvent(new Event(TOUR_OWN_JOB_EVENT))}
                >
                  Start my own job
                </button>
              )
            ) : (
              <>
                {/* PAIGE: Next is there while the five are still exporting, so nobody waits on them. */}
                {step.next && (ready || step.download) && (
                  <button
                    type="button"
                    className="secondary-button small"
                    data-telemetry="tour-next"
                    onClick={() => advanceTour(tour.step, { exported: ownExport.exported }, 'next')}
                  >
                    Next <ArrowRight size={14} aria-hidden="true" />
                  </button>
                )}
                {step.download && ready && (
                  <TourDownload
                    jobId={tour.jobId}
                    total={context.total}
                    onSaved={() => advanceTour(tour.step)}
                  />
                )}
                {step.openExport && (
                  <button
                    type="button"
                    className="primary-button small"
                    data-telemetry="tour-open-export"
                    onClick={() => void openExport()}
                  >
                    {ownExport.folder ? (
                      <>
                        <FolderOpen size={14} aria-hidden="true" />
                        Open folder
                      </>
                    ) : !hostHas('desktop-export') ? (
                      // PAIGE: the job's Export, where its destinations and Download are.
                      <>
                        <FolderOutput size={14} aria-hidden="true" />
                        Open Export
                      </>
                    ) : (
                      <>
                        <History size={14} aria-hidden="true" />
                        See activity
                      </>
                    )}
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </section>
    </>
  );
}

type Box = { left: number; top: number; width: number; height: number };
const drawn = (selector: string) =>
  [...document.querySelectorAll<HTMLElement>(selector)].filter(
    (element) => element.getClientRects().length > 0,
  );
/** The pane that scrolls to bring this into view. */
const paneOf = (element: HTMLElement) => {
  for (let at = element.parentElement; at; at = at.parentElement)
    if (at.scrollHeight > at.clientHeight && /auto|scroll/.test(getComputedStyle(at).overflowY))
      return at;
  return null;
};
/**
 * Really on screen: all of it inside the window and inside the pane that scrolls it, and what shows
 * at its middle is the control itself, not a window opened over it (Job setup, a menu). The glow never
 * sits on something half scrolled away, off screen, or under something else. (It takes no pointer, so it isn't hit.)
 */
const showing = (element: HTMLElement) => {
  const rect = element.getBoundingClientRect();
  if (
    rect.width === 0 ||
    rect.top < -1 ||
    rect.left < -1 ||
    rect.bottom > window.innerHeight + 1 ||
    rect.right > window.innerWidth + 1
  )
    return false;
  const pane = paneOf(element)?.getBoundingClientRect();
  if (pane && (rect.top < pane.top - 1 || rect.bottom > pane.bottom + 1)) return false;
  const hit = document.elementFromPoint(rect.left + rect.width / 2, rect.top + rect.height / 2);
  return Boolean(hit && (element.contains(hit) || hit.contains(element)));
};
const onScreen = (selector: string) => drawn(selector).find(showing);
/** The one nearest the middle of the window, so the walk scrolls the least to reach it. */
const nearest = (elements: HTMLElement[]) => {
  const off = (element: HTMLElement) => {
    const rect = element.getBoundingClientRect();
    return Math.abs(rect.top + rect.height / 2 - window.innerHeight / 2);
  };
  return elements.reduce<HTMLElement | undefined>(
    (best, element) => (!best || off(element) < off(best) ? element : best),
    undefined,
  );
};
const reducedMotion = () =>
  Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
const SCROLL_KEYS = new Set(['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ']);
/** How long a pane must be still before the ring measures again, and how long after the walk's scroll the list may take to draw the control (ms). */
const SETTLED = 150;
const DRAWN_WITHIN = 700;
/** A click still doing its work: a menu or window it opened is up, or the control says it's busy. */
const OPEN = '[role="menu"], [role="dialog"], [aria-modal="true"], dialog[open]';
const BUSY = '[aria-expanded="true"], [aria-busy="true"], .spin';
const working = (control: HTMLElement, button: HTMLElement | null) =>
  drawn(OPEN).length > 0 ||
  Boolean(button?.isConnected && button.matches(`:disabled, ${BUSY}`)) ||
  Boolean(control.isConnected && (control.matches(BUSY) || control.querySelector(BUSY)));
/** The step a click is moving on from, while it waits. */
let settling: TourStepId | null = null;
/**
 * Moves on from `from` once the click has done its work and `after` ms more have passed. It lives
 * outside the card, which the workspace may draw again elsewhere meanwhile (an export empties the
 * list), and gives up when the step changes first (Next, Back, Leave tour).
 */
function advanceOnceSettled(
  from: TourStepId,
  control: HTMLElement,
  button: HTMLElement | null,
  after: number,
): void {
  if (settling === from) return;
  settling = from;
  let quiet = 0;
  const timer = window.setInterval(() => {
    if (readTour()?.step !== from) {
      window.clearInterval(timer);
      settling = null;
      return;
    }
    quiet = working(control, button) ? 0 : quiet + 100;
    if (quiet < after) return;
    window.clearInterval(timer);
    settling = null;
    advanceTour(from, {}, 'control');
  }, 100);
}

/**
 * The glow over the real controls the current step names, and the listener that moves the
 * tour on when the step's control is used. Drawn above everything; the card renders one per step.
 */
// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
function TourOverlay() {
  const tour = useTour();
  const step = tour ? tourStep(tour.step, Boolean(tour.own)) : null;
  const targets = step?.targets ?? [];
  // Walk from target to target, then rest on the last. Reduced motion rests on the last at once,
  // unless the step shows its controls in turn.
  const [walking] = useState(() => targets.length > 1 && Boolean(step?.inTurn || !reducedMotion()));
  const [stop, setStop] = useState(() => (walking ? 0 : Math.max(0, targets.length - 1)));
  const [box, setBox] = useState<Box | null>(null);
  // When a pane holding the step's controls last scrolled by itself (the walk, or the list bringing
  // a document into view), and whether the walk is waiting for the list to draw what it scrolled to.
  const moving = useRef(-Infinity);
  const seeking = useRef(false);
  // What the person did during this step: where they scrolled, clicked or pressed a scrolling key,
  // whether they moved the pointer, and when they last scrolled or pressed anything.
  const person = useRef({ used: new Set<Node>(), moved: false, at: -Infinity });
  // A step that needs a filter chooses it once when it opens (choosing it again refreshes the list). The tour's
  // click, not the person's: telemetry doesn't count it.
  useEffect(() => {
    const filter = step?.select ? document.querySelector<HTMLElement>(step.select) : null;
    if (filter) clickWithoutTelemetry(filter);
  }, [tour?.step]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const seen = person.current;
    const watched = [...targets, ...Object.values(step?.spots ?? {})].join(', ');
    const use = (event: Event) => {
      if (
        !(event.target instanceof Node) ||
        (event instanceof KeyboardEvent && !SCROLL_KEYS.has(event.key))
      )
        return;
      seen.used.add(event.target);
      seen.at = performance.now();
    };
    const move = (event: PointerEvent) => {
      if (event.movementX || event.movementY) seen.moved = true;
      if (event.buttons) seen.at = performance.now();
    };
    // Scrolling the person didn't do carries the controls away from where the ring is.
    const scrolled = (event: Event) => {
      if (!watched || performance.now() - seen.at < 700) return;
      if (
        event.target instanceof Document ||
        (event.target instanceof Element && event.target.querySelector(watched))
      )
        moving.current = performance.now();
    };
    const kinds = ['wheel', 'touchstart', 'pointerdown', 'keydown'];
    for (const kind of kinds)
      document.addEventListener(kind, use, { capture: true, passive: true });
    document.addEventListener('pointermove', move, { capture: true, passive: true });
    document.addEventListener('scroll', scrolled, { capture: true, passive: true });
    return () => {
      for (const kind of kinds) document.removeEventListener(kind, use, { capture: true });
      document.removeEventListener('pointermove', move, { capture: true });
      document.removeEventListener('scroll', scrolled, { capture: true });
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!walking) return;
    const timer = window.setInterval(
      () => setStop((current) => (current + 1 < targets.length ? current + 1 : current)),
      step?.walkEvery ?? 1700,
    );
    return () => window.clearInterval(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Bring each stop into the middle of its pane as the glow reaches it, smoothly (at once with reduced
  // motion). Not in a pane the person has scrolled or clicked in during this step, or is pointing in: they lead there.
  useEffect(() => {
    const selector = targets[stop];
    if (!selector || onScreen(selector)) return;
    const spot = step?.spots?.[selector];
    const goal =
      (spot ? document.querySelector<HTMLElement>(spot) : null) ?? nearest(drawn(selector));
    const pane = goal && paneOf(goal);
    if (
      !goal ||
      (pane &&
        ([...person.current.used].some((node) => pane.contains(node)) ||
          (person.current.moved && pane.matches(':hover'))))
    )
      return;
    moving.current = performance.now();
    seeking.current = true;
    goal.scrollIntoView({
      block: 'center',
      inline: 'nearest',
      behavior: reducedMotion() ? 'auto' : 'smooth',
    });
  }, [tour?.step, stop]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!targets.length) return;
    let frame = 0;
    const place = () => {
      frame = window.requestAnimationFrame(place);
      const now = performance.now();
      // While a pane scrolls by itself the ring waits where it is; once it has come to rest the ring
      // measures again and moves to where the control is then (Brandy, 2026-10-05: it floated off).
      if (now - moving.current < SETTLED) return;
      const at = Math.min(stop, targets.length - 1);
      let target = onScreen(targets[at]);
      if (target) seeking.current = false;
      else if (seeking.current && now - moving.current < DRAWN_WITHIN) return;
      // A stop that isn't on screen hands the glow to the nearest one before it that is.
      for (let back = at - 1; back >= 0 && !target; back -= 1) target = onScreen(targets[back]);
      const rect = target?.getBoundingClientRect();
      setBox((current) => {
        if (!rect) return null;
        const next = {
          left: Math.round(rect.left) - 5,
          top: Math.round(rect.top) - 5,
          width: Math.round(rect.width) + 10,
          height: Math.round(rect.height) + 10,
        };
        return current &&
          current.left === next.left &&
          current.top === next.top &&
          current.width === next.width &&
          current.height === next.height
          ? current
          : next;
      });
    };
    place();
    return () => window.cancelAnimationFrame(frame);
  }, [tour?.step, stop]); // eslint-disable-line react-hooks/exhaustive-deps
  // The scissors and paperclip normally show only on pointing; this step keeps them shown.
  useEffect(() => {
    document.body.classList.toggle('tour-show-starts', Boolean(step?.showStarts));
    return () => document.body.classList.remove('tour-show-starts');
  }, [step?.showStarts]);
  // Clicking the step's control moves the tour on: once the click has done its work (a menu or
  // window it opened has closed, nothing it started is busy), and a moment more, so they see it.
  useEffect(() => {
    const selector = step ? advanceOnOf(step) : null;
    if (!selector) return;
    const from = tour!.step;
    const used = (event: MouseEvent) => {
      const control =
        event.target instanceof Element ? event.target.closest<HTMLElement>(selector) : null;
      if (control)
        advanceOnceSettled(
          from,
          control,
          (event.target as Element).closest<HTMLElement>('button, [role="tab"], a'),
          step?.advanceAfter ?? ADVANCE_AFTER,
        );
    };
    document.addEventListener('click', used, true);
    return () => document.removeEventListener('click', used, true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!tour || !box || !tour.ready) return null;
  return createPortal(
    <div key={tour.step} className="tour-glow" aria-hidden="true" style={box} />,
    document.body,
  );
}

/** Re-reads the milestones main keeps (loaded a moment after start) whenever they change. */
function useMilestones(): { known: boolean; exported: boolean } {
  const read = () => ({
    known: hasMilestone('firstLaunch'),
    exported: hasMilestone('firstExport'),
  });
  const [state, setState] = useState(read);
  useEffect(() => {
    const update = () => setState(read());
    update();
    window.addEventListener(MILESTONE_EVENT, update);
    return () => window.removeEventListener(MILESTONE_EVENT, update);
  }, []);
  return state;
}

/**
 * Their first own job offers the tour once, when it has finished reading (shouldOfferOwnTour):
 * Show me around runs the same ten steps on their documents; No thanks leaves them to the hints.
 * Nothing is offered until main's milestones have arrived, so nobody who has exported before
 * sees it even for a moment.
 */
export function TourOffer({
  jobId,
  documents,
  reading,
  sample,
}: {
  jobId: string;
  documents: number;
  reading: boolean;
  sample: boolean;
}) {
  const tour = useTour();
  const milestones = useMilestones();
  const [answered, setAnswered] = useState(() => readTourFlag(TOUR_OFFER_KEY));
  const offered =
    milestones.known &&
    shouldOfferOwnTour({
      tour,
      sample,
      reading,
      documents,
      answered,
      taken: readTourFlag(TOUR_TAKEN_KEY),
      exportedBefore: milestones.exported,
    });
  useEffect(() => {
    if (offered) tourTelemetry.offer('shown', jobId);
  }, [offered, jobId]);
  if (!offered) return null;
  const answer = (show: boolean) => {
    markTourFlag(TOUR_OFFER_KEY);
    setAnswered(true);
    tourTelemetry.offer(show ? 'yes' : 'no', jobId);
    // Either answer is announced: No thanks lets the hints speak.
    if (show) {
      tourTelemetry.started('own-offer', { jobId, again: readTourFlag(TOUR_WATCHED_KEY) });
      writeTour({
        jobId,
        step: 'pane',
        startedAt: new Date().toISOString(),
        own: true,
        ready: true,
      });
    } else window.dispatchEvent(new Event(TOUR_EVENT));
  };
  return (
    <section className="tour-card" aria-label="Tour">
      <header>
        <span className="tour-title">
          <span className="tour-mark" aria-hidden="true">
            <Play size={11} />
          </span>
          Tour
        </span>
      </header>
      <p className="tour-say">
        Paige read your{' '}
        {documents === 1 ? 'document' : `${documents.toLocaleString('en-US')} documents`}. Want a
        quick tour of this screen, using {documents === 1 ? 'it' : 'them'}? You can leave any time.
      </p>
      <div className="tour-actions">
        <button
          type="button"
          className="quiet-link"
          data-telemetry="tour-offer-no"
          onClick={() => answer(false)}
        >
          No thanks
        </button>
        <button
          type="button"
          className="primary-button small"
          data-telemetry="tour-offer-yes"
          onClick={() => answer(true)}
        >
          Show me around
        </button>
      </div>
    </section>
  );
}
